import * as React from "react"
import styled from "styled-components"
import { bodyFontFamily, colors, scale } from "../../styles"
import { Flex } from "../Flex"
import { Text } from "../Text"
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form'

export interface TextFieldInputProps
	extends Omit<React.HTMLProps<HTMLInputElement>, "ref" | "as" | "size"> {
	error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>
	label: string
	inputRef?: React.Ref<HTMLInputElement>
	isHoneypot?: boolean
}

export interface TextFieldProps extends TextFieldInputProps {
	helperText?: any
}

const TextFieldStyled = styled.input.attrs<TextFieldInputProps>((props) => ({
	"aria-disabled": props.disabled,
})) <TextFieldInputProps>`
	width: 100%;
	padding: ${scale.px(1.75)} ${scale.px(1.75)} ${scale.px(1.5)};
	font-family: ${bodyFontFamily};
	font-size: ${scale.px(2)};
	background-color: ${colors.white};
	border: solid ${(props) => colors[props.error ? "alert" : "blue33"]} 1px;
	border-radius: ${scale.px(0.5)};
	color: ${(props) => colors[props.error ? "alert" : "navy"]};
	outline: none;

	* {
		transition: all 0.3s;
	}

	::placeholder {
		color: ${colors.placeholder};
	}

	&:focus {
		border-color: ${(props) => colors[props.error ? "alert" : "navy"]};
	}

	&:disabled {
		background: rgba(255, 255, 255, 0.3);
		cursor: not-allowed;
		color: ${colors.navy};
		-webkit-text-fill-color: ${colors.navy};
		opacity: 1;
	}

	/* overide any browser specific or plugin prefill styles */
	&[style] {
		background: ${colors.white} !important;
	}

	// Text for date field
	&::-webkit-datetime-edit-fields-wrapper {
		color: ${colors.navy};
	}
	// Date icon in Chrome
	&::-webkit-calendar-picker-indicator {
		margin-right: -5px;
	}
`

const Label = styled.label`
	pointer-events: none;
	font-size: ${scale.px(1.75)};
	line-height: ${scale.px(2.5)};
	margin-bottom: ${scale.px(0.5)};
`

const TextField: React.FC<TextFieldProps> = ({
	inputRef,
	error,
	label,
	helperText,
	placeholder,
	isHoneypot,
	...rest
}) => {
	return (
		<Flex
			w={1}
			flexDirection="column"
			className={isHoneypot && "visually-hidden"}
		>
			<Label htmlFor={rest.id}>{label}</Label>
			<Flex position="relative">
				<TextFieldStyled
					label={label}
					error={error}
					aria-invalid={error ? "true" : "false"}
					ref={inputRef}
					placeholder={placeholder}
					{...rest}
				/>
			</Flex>
			<Flex
				w={1}
				minh={helperText ? "none" : 2}
				mb={1}
				justifyContent="flex-end"
			>
				<Text
					as="div"
					textAlign="right"
					textColor={error ? "alert" : "navy"}
					fontSize={1.75}
					lineHeight={2.5}
					mt={0.5}
					mb={0.5}
				>
					<>
						{helperText}
					</>
				</Text>
			</Flex>
		</Flex>
	)
}

export { TextField }
